import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../components/Layout/DashboardLayout";
import Spinner from "../components/ui/Spinner";
import { dashboardApi, DashboardSummaryResponse } from "../api/dashboardApi";

function formatSignedPts(n: number): string {
  const v = n.toFixed(0);
  if (n > 0) {
    return `+${v}`;
  }
  if (n < 0) {
    return v;
  }
  return "+0";
}

function formatMoMPercent(n: number): string {
  if (n > 0) {
    return `+${n.toFixed(0)}% MoM`;
  }
  if (n < 0) {
    return `${n.toFixed(0)}% MoM`;
  }
  return "0% MoM";
}

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<DashboardSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await dashboardApi.getSummary();
        setSummary(data);
      } catch {
        setError("Could not load dashboard. Check your connection and try again.");
        setSummary(null);
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, []);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center py-16">
          <Spinner className="h-10 w-10" label="Loading dashboard…" />
        </div>
      </DashboardLayout>
    );
  }

  if (error || !summary) {
    return (
      <DashboardLayout>
        <div className="rounded-xl border border-amber-500/40 bg-amber-950/30 px-4 py-6 text-center text-sm text-amber-100">
          {error || "Unable to load dashboard."}
        </div>
      </DashboardLayout>
    );
  }

  const stats = [
    {
      label: "Active Clients",
      value: String(summary.activeClients),
      delta: `+${summary.activeClientsAddedThisMonth} this month`,
    },
    {
      label: "Avg Score Gain",
      value: `${formatSignedPts(summary.avgScoreGain)} pts`,
      delta: `${formatSignedPts(summary.avgScoreGainVsLastMonth)} pts vs last month`,
    },
    {
      label: "Items Resolved",
      value: String(summary.itemsResolved),
      delta: `+${summary.itemsResolvedThisWeek} this week`,
    },
    {
      label: "Monthly Revenue",
      value: `$${Number(summary.monthlyRevenue).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
      delta: formatMoMPercent(summary.revenueGrowthPercent),
    },
  ];

  return (
    <DashboardLayout>
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="space-y-6 xl:col-span-2">
          <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {stats.map((stat) => (
              <div key={stat.label} className="rounded-xl border border-slate-800 bg-slate-900 p-5">
                <p className="text-sm text-slate-400">{stat.label}</p>
                <p className="mt-2 text-3xl font-bold text-white">{stat.value}</p>
                <p className="mt-1 text-xs text-blue-300">{stat.delta}</p>
              </div>
            ))}
          </section>

          <section className="rounded-xl border border-slate-800 bg-slate-900 p-5">
            <h3 className="mb-4 text-lg font-semibold text-white">Recent Clients</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-800 text-slate-400">
                    <th className="pb-3">Name</th>
                    <th className="pb-3">Current Score</th>
                    <th className="pb-3">Status</th>
                    <th className="pb-3">Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.recentClients.length === 0 && (
                    <tr>
                      <td colSpan={4} className="py-8 text-center text-slate-500">
                        No clients to show yet.
                      </td>
                    </tr>
                  )}
                  {summary.recentClients.map((client) => (
                    <tr
                      key={client.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => navigate(`/clients/${client.id}`)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          navigate(`/clients/${client.id}`);
                        }
                      }}
                      className="cursor-pointer border-b border-slate-800/60 text-slate-200 hover:bg-slate-800/50"
                    >
                      <td className="py-3 font-medium text-white">{client.name}</td>
                      <td className="py-3">{client.currentScore}</td>
                      <td className="py-3">
                        <span className="rounded-full bg-blue-600/20 px-2 py-1 text-xs text-blue-300">
                          {client.status}
                        </span>
                      </td>
                      <td className="py-3 text-slate-400">{client.updatedAgo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        <aside className="rounded-xl border border-slate-800 bg-slate-900 p-5">
          <h3 className="text-lg font-semibold text-white">AI Insights</h3>
          <div className="mt-4 space-y-4 text-sm">
            {summary.insights.map((insight, i) => (
              <div
                key={`${insight.title}-${i}`}
                className={`rounded-lg border p-3 ${
                  insight.type === "warning"
                    ? "border-amber-500/30 bg-amber-500/5"
                    : insight.type === "success"
                      ? "border-emerald-500/30 bg-emerald-500/5"
                      : "border-blue-500/30 bg-blue-500/5"
                }`}
              >
                <p
                  className={`font-semibold ${
                    insight.type === "warning"
                      ? "text-amber-200"
                      : insight.type === "success"
                        ? "text-emerald-200"
                        : "text-blue-300"
                  }`}
                >
                  {insight.title}
                </p>
                <p className="mt-1 text-slate-300">{insight.message}</p>
              </div>
            ))}
          </div>
        </aside>
      </div>
    </DashboardLayout>
  );
};

export default DashboardPage;
